import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Tag as TagIcon,
  Plus,
  RefreshCw,
  AlertTriangle,
  PowerOff,
  Power,
  Edit3,
  GitMerge,
  Save,
  X,
  Search,
  Info,
} from 'lucide-react';
import api from '../../api';
import toast from 'react-hot-toast';
import { invalidateClientTagCache } from '../ClientTagPicker';

/**
 * Admin → AI Configuration → Client Tags
 *
 * Master list management for client engagement tags. Admin-only.
 * Every mutation hits /api/admin/client-tags/* which writes an audit
 * log row. Historical references are preserved via deactivate-rather-
 * than-delete and via merge (which re-points every reference in a
 * single transaction on the server).
 */

interface Tag {
  tag_id: number;
  tag_name: string;
  tag_type: 'client' | 'internal' | 'rnd';
  active: boolean;
  display_order: number;
  created_at: string;
  updated_at: string;
}

interface StalenessEntry {
  table: string;
  modules: string[];
  untagged: number;
}

interface TagUsage {
  [tableName: string]: number;
}

const TYPE_LABEL: Record<Tag['tag_type'], string> = {
  client: 'Client',
  internal: 'Internal',
  rnd: 'R&D / Learning',
};

const TYPE_BADGE: Record<Tag['tag_type'], string> = {
  client: 'bg-blue-100 text-blue-800',
  internal: 'bg-purple-100 text-purple-800',
  rnd: 'bg-amber-100 text-amber-800',
};

const ClientTagManagement: React.FC = () => {
  const [tags, setTags] = useState<Tag[]>([]);
  const [staleness, setStaleness] = useState<StalenessEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<'all' | Tag['tag_type']>('all');
  const [includeInactive, setIncludeInactive] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingTag, setEditingTag] = useState<Tag | null>(null);
  const [mergeSource, setMergeSource] = useState<Tag | null>(null);
  const [usageByTag, setUsageByTag] = useState<Record<number, TagUsage>>({});

  const fetchTags = useCallback(async () => {
    setLoading(true);
    try {
      const [tagsRes, stalenessRes] = await Promise.all([
        api.get(`/api/admin/client-tags?includeInactive=${includeInactive ? '1' : '0'}`),
        api.get('/api/admin/client-tags/untagged-summary'),
      ]);
      let loadedTags: Tag[] = [];
      if (tagsRes.data && tagsRes.data.success) {
        loadedTags = tagsRes.data.tags || [];
        setTags(loadedTags);
      }
      if (stalenessRes.data && stalenessRes.data.success) {
        setStaleness((stalenessRes.data.summary || []).filter((e: StalenessEntry) => e.untagged > 0));
      }
      invalidateClientTagCache();

      // Auto-load per-tag usage counts so the column shows real numbers
      // without a manual "Show usage" click. Runs in parallel — each
      // query is a single row count across ~7 small tables.
      if (loadedTags.length > 0) {
        const pairs = await Promise.all(
          loadedTags.map(async (t) => {
            try {
              const r = await api.get(`/api/admin/client-tags/${t.tag_id}/usage`);
              return r.data && r.data.success ? [t.tag_id, r.data.usage || {}] as const : null;
            } catch (_) {
              return null;
            }
          })
        );
        const next: Record<number, TagUsage> = {};
        for (const p of pairs) if (p) next[p[0]] = p[1];
        setUsageByTag(next);
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.error || err?.message || 'Failed to load client tags');
    } finally {
      setLoading(false);
    }
  }, [includeInactive]);

  useEffect(() => {
    fetchTags();
  }, [fetchTags]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return tags.filter((t) => {
      if (typeFilter !== 'all' && t.tag_type !== typeFilter) return false;
      if (!term) return true;
      return t.tag_name.toLowerCase().includes(term);
    });
  }, [tags, search, typeFilter]);

  const handleDeactivate = async (tag: Tag) => {
    if (!window.confirm(`Deactivate "${tag.tag_name}"? It will be hidden from dropdowns but historical data stays attributed to it.`)) return;
    try {
      const res = await api.post(`/api/admin/client-tags/${tag.tag_id}/deactivate`, {});
      if (res.data && res.data.success) {
        toast.success('Tag deactivated');
        fetchTags();
      } else {
        toast.error(res.data?.error || 'Deactivation failed');
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.error || err?.message || 'Deactivation failed');
    }
  };

  const handleReactivate = async (tag: Tag) => {
    try {
      const res = await api.post(`/api/admin/client-tags/${tag.tag_id}/reactivate`, {});
      if (res.data && res.data.success) {
        toast.success('Tag reactivated');
        fetchTags();
      } else {
        toast.error(res.data?.error || 'Reactivation failed');
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.error || err?.message || 'Reactivation failed');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-2">
              <TagIcon className="w-6 h-6 text-blue-600" />
              <h2 className="text-2xl font-bold text-gray-900">Client Tags</h2>
            </div>
            <p className="text-gray-600 mt-1 max-w-3xl">
              Master list of client engagement tags used across every Jarvis module. Every container
              (Council session, SOP project, workspace, engagement) must carry one of these tags so
              downstream usage and cost can be attributed back to the right client.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={fetchTags}
              className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={() => setShowCreate(true)}
              className="flex items-center gap-2 px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700"
            >
              <Plus className="w-4 h-4" />
              Add tag
            </button>
          </div>
        </div>
      </div>

      {/* Untagged containers warning */}
      {staleness.length > 0 && (
        <div className="bg-amber-50 border border-amber-300 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <div className="font-semibold text-amber-900">Untagged containers detected</div>
              <p className="text-sm text-amber-800 mt-1">
                Existing rows without a client tag predate this feature. Users will be prompted to
                tag them on first access. Counts below are informational. 
              </p>
              <div className="mt-3 space-y-1">
                {staleness.map((e) => (
                  <div key={e.table} className="flex items-center justify-between bg-white border border-amber-200 rounded-lg px-3 py-2 text-sm">
                    <span>
                      <span className="font-mono">{e.table}</span>
                      <span className="ml-3 text-gray-500">({e.modules.join(', ')})</span>
                    </span>
                    <span className="font-semibold">{e.untagged.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex gap-3 flex-wrap items-center">
          <div className="flex-1 min-w-[220px] relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search tag name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as any)}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All types</option>
            <option value="client">Client</option>
            <option value="internal">Internal</option>
            <option value="rnd">R&D / Learning</option>
          </select>
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
              className="rounded"
            />
            Show inactive
          </label>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">Tag</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">Type</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">Order</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">Status</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">Usage</th>
              <th className="px-4 py-3 text-right font-semibold text-gray-700">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-gray-500">
                <RefreshCw className="w-5 h-5 inline animate-spin mr-2" /> Loading…
              </td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-gray-500">No tags match your filters.</td></tr>
            )}
            {!loading && filtered.map((tag) => {
              const usage = usageByTag[tag.tag_id];
              // Separate container count (projects, sessions, engagements, etc.)
              // from LLM event count so the display is meaningful at a glance.
              const eventCount = usage ? Number(usage.usage_events || 0) : 0;
              const containerCount = usage
                ? Object.entries(usage).reduce((sum, [k, v]) => (k === 'usage_events' ? sum : sum + Number(v || 0)), 0)
                : 0;
              const hasUsage = usage !== undefined;
              return (
                <tr key={tag.tag_id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-900">{tag.tag_name}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-block text-xs px-2 py-0.5 rounded-full ${TYPE_BADGE[tag.tag_type]}`}>
                      {TYPE_LABEL[tag.tag_type]}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-600 font-mono">{tag.display_order}</td>
                  <td className="px-4 py-3">
                    {tag.active ? (
                      <span className="inline-block text-xs px-2 py-0.5 bg-green-100 text-green-800 rounded-full">Active</span>
                    ) : (
                      <span className="inline-block text-xs px-2 py-0.5 bg-gray-200 text-gray-700 rounded-full">Inactive</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {!hasUsage ? (
                      <span className="text-xs text-gray-400 inline-flex items-center gap-1">
                        <RefreshCw className="w-3 h-3 animate-spin" />
                        Loading…
                      </span>
                    ) : containerCount === 0 && eventCount === 0 ? (
                      <span className="text-xs text-gray-400">No usage yet</span>
                    ) : (
                      <div className="text-xs space-y-0.5">
                        <div className="text-gray-900">
                          <span className="font-semibold">{containerCount.toLocaleString()}</span>
                          <span className="text-gray-500"> container{containerCount === 1 ? '' : 's'}</span>
                        </div>
                        <div className="text-gray-700">
                          <span className="font-semibold">{eventCount.toLocaleString()}</span>
                          <span className="text-gray-500"> LLM call{eventCount === 1 ? '' : 's'}</span>
                        </div>
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-1">
                      <button
                        onClick={() => setEditingTag(tag)}
                        className="p-1.5 text-gray-600 hover:text-blue-700 hover:bg-blue-50 rounded"
                        title="Edit"
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setMergeSource(tag)}
                        className="p-1.5 text-gray-600 hover:text-purple-700 hover:bg-purple-50 rounded"
                        title="Merge into another tag"
                      >
                        <GitMerge className="w-4 h-4" />
                      </button>
                      {tag.active ? (
                        <button
                          onClick={() => handleDeactivate(tag)}
                          className="p-1.5 text-gray-600 hover:text-red-700 hover:bg-red-50 rounded"
                          title="Deactivate"
                        >
                          <PowerOff className="w-4 h-4" />
                        </button>
                      ) : (
                        <button
                          onClick={() => handleReactivate(tag)}
                          className="p-1.5 text-gray-600 hover:text-green-700 hover:bg-green-50 rounded"
                          title="Reactivate"
                        >
                          <Power className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-900 flex gap-3">
        <Info className="w-5 h-5 flex-shrink-0 mt-0.5" />
        <div>
          <div className="font-semibold">Why tags are never hard-deleted</div>
          <p className="mt-1">
            Historical usage events reference tag_ids for cost attribution. Deactivating hides a tag
            from dropdowns while preserving every past reference. If you created a duplicate by
            accident (e.g. &quot;AmEx&quot; and &quot;American Express&quot;), use <strong>Merge</strong> to
            re-point all references in a single transaction and deactivate the source automatically.
          </p>
        </div>
      </div>

      {showCreate && <CreateTagModal onClose={() => setShowCreate(false)} onSaved={() => { setShowCreate(false); fetchTags(); }} />}
      {editingTag && <EditTagModal tag={editingTag} onClose={() => setEditingTag(null)} onSaved={() => { setEditingTag(null); fetchTags(); }} />}
      {mergeSource && (
        <MergeTagModal
          source={mergeSource}
          candidates={tags.filter((t) => t.tag_id !== mergeSource.tag_id && t.active)}
          onClose={() => setMergeSource(null)}
          onMerged={() => { setMergeSource(null); fetchTags(); }}
        />
      )}
    </div>
  );
};

// ─── Create modal ──────────────────────────────────────────────────────────

const CreateTagModal: React.FC<{ onClose: () => void; onSaved: () => void }> = ({ onClose, onSaved }) => {
  const [name, setName] = useState('');
  const [type, setType] = useState<Tag['tag_type']>('client');
  const [order, setOrder] = useState<number | ''>('');
  const [saving, setSaving] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return toast.error('Name required');
    setSaving(true);
    try {
      const res = await api.post('/api/admin/client-tags', {
        tag_name: name.trim(),
        tag_type: type,
        display_order: order === '' ? undefined : Number(order),
        active: true,
      });
      if (res.data && res.data.success) {
        toast.success('Tag created');
        onSaved();
      } else {
        toast.error(res.data?.error || 'Create failed');
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.error || err?.message || 'Create failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="New client tag" onClose={onClose}>
      <form onSubmit={submit} className="space-y-4 p-6">
        <Field label="Tag name" required>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
            placeholder="e.g. Goldman Sachs"
            required
          />
        </Field>
        <Field label="Type" required>
          <select
            value={type}
            onChange={(e) => setType(e.target.value as Tag['tag_type'])}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
          >
            <option value="client">Client</option>
            <option value="internal">Internal</option>
            <option value="rnd">R&D / Learning</option>
          </select>
        </Field>
        <Field label="Display order" helpText="Lower numbers appear first within their type. Leave blank to append at end.">
          <input
            type="number"
            min="0"
            value={order}
            onChange={(e) => setOrder(e.target.value === '' ? '' : Number(e.target.value))}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg font-mono"
            placeholder="Auto"
          />
        </Field>
        <FormActions onCancel={onClose} saving={saving} />
      </form>
    </Modal>
  );
};

// ─── Edit modal ────────────────────────────────────────────────────────────

const EditTagModal: React.FC<{ tag: Tag; onClose: () => void; onSaved: () => void }> = ({ tag, onClose, onSaved }) => {
  const [name, setName] = useState(tag.tag_name);
  const [type, setType] = useState<Tag['tag_type']>(tag.tag_type);
  const [order, setOrder] = useState<number>(tag.display_order);
  const [saving, setSaving] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return toast.error('Name required');
    setSaving(true);
    try {
      const res = await api.patch(`/api/admin/client-tags/${tag.tag_id}`, {
        tag_name: name.trim(),
        tag_type: type,
        display_order: Number(order),
      });
      if (res.data && res.data.success) {
        toast.success('Tag updated');
        onSaved();
      } else {
        toast.error(res.data?.error || 'Update failed');
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.error || err?.message || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={`Edit "${tag.tag_name}"`} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4 p-6">
        <Field label="Tag name" required>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
            required
          />
        </Field>
        <Field label="Type" required>
          <select
            value={type}
            onChange={(e) => setType(e.target.value as Tag['tag_type'])}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
          >
            <option value="client">Client</option>
            <option value="internal">Internal</option>
            <option value="rnd">R&D / Learning</option>
          </select>
        </Field>
        <Field label="Display order">
          <input
            type="number"
            min="0"
            value={order}
            onChange={(e) => setOrder(Number(e.target.value))}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg font-mono"
          />
        </Field>
        <FormActions onCancel={onClose} saving={saving} />
      </form>
    </Modal>
  );
};

// ─── Merge modal ───────────────────────────────────────────────────────────

const MergeTagModal: React.FC<{
  source: Tag;
  candidates: Tag[];
  onClose: () => void;
  onMerged: () => void;
}> = ({ source, candidates, onClose, onMerged }) => {
  const [targetId, setTargetId] = useState<number | ''>('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!targetId) return toast.error('Pick a target tag');
    if (!window.confirm(`Merge "${source.tag_name}" into the selected tag? This re-points every reference and deactivates "${source.tag_name}". It cannot be undone.`)) return;
    setSubmitting(true);
    try {
      const res = await api.post('/api/admin/client-tags/merge', {
        source_tag_id: source.tag_id,
        target_tag_id: Number(targetId),
      });
      if (res.data && res.data.success) {
        const counts = res.data.counts || {};
        const total = Object.values(counts).reduce((a: number, b: any) => a + Number(b || 0), 0);
        toast.success(`Merged — ${total.toLocaleString()} reference${total === 1 ? '' : 's'} re-pointed`);
        onMerged();
      } else {
        toast.error(res.data?.error || 'Merge failed');
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.error || err?.message || 'Merge failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal title={`Merge "${source.tag_name}" into another tag`} onClose={onClose}>
      <div className="space-y-4 p-6">
        <div className="text-sm text-gray-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
          <AlertTriangle className="inline w-4 h-4 mr-1 text-amber-600" />
          All containers + usage_events + user preferences currently pointing at <strong>{source.tag_name}</strong> will
          be re-pointed to your target, and <strong>{source.tag_name}</strong> will be deactivated. Audit-logged.
        </div>
        <Field label="Target tag" required>
          <select
            value={targetId}
            onChange={(e) => setTargetId(e.target.value === '' ? '' : Number(e.target.value))}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
          >
            <option value="">Select target…</option>
            {candidates.map((c) => (
              <option key={c.tag_id} value={c.tag_id}>{c.tag_name} ({TYPE_LABEL[c.tag_type]})</option>
            ))}
          </select>
        </Field>
        <div className="flex justify-end gap-2 pt-4 border-t border-gray-200">
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={submitting || !targetId}
            className="flex items-center gap-2 px-4 py-2 text-sm text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:bg-purple-300"
          >
            <GitMerge className="w-4 h-4" />
            {submitting ? 'Merging…' : 'Merge'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

// ─── Shared UI pieces ──────────────────────────────────────────────────────

const Modal: React.FC<{ title: string; onClose: () => void; children: React.ReactNode }> = ({ title, onClose, children }) => (
  <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
          <X className="w-5 h-5" />
        </button>
      </div>
      {children}
    </div>
  </div>
);

const Field: React.FC<{ label: string; required?: boolean; helpText?: string; children: React.ReactNode }> = ({ label, required, helpText, children }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {label}{required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    {children}
    {helpText && <p className="mt-1 text-xs text-gray-500">{helpText}</p>}
  </div>
);

const FormActions: React.FC<{ onCancel: () => void; saving: boolean }> = ({ onCancel, saving }) => (
  <div className="flex justify-end gap-2 pt-4 border-t border-gray-200">
    <button
      type="button"
      onClick={onCancel}
      disabled={saving}
      className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
    >
      Cancel
    </button>
    <button
      type="submit"
      disabled={saving}
      className="flex items-center gap-2 px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:bg-blue-300"
    >
      <Save className="w-4 h-4" />
      {saving ? 'Saving…' : 'Save'}
    </button>
  </div>
);

export default ClientTagManagement;
